import React, {
  FunctionComponent,
  useState,
  useContext,
  useEffect,
} from "react";
import { Dialog, Button } from "@cmsgov/ds-medicare-gov";
import { CountyPicker } from ".";
import { sendTealiumEvent, useTranslate } from "../helpers";
import classNames from "classnames";
import { County, ActionType } from "../@types";
import { AppStore } from "../app/store";
import { withRouter, RouteComponentProps } from "react-router";
import URI from "urijs";
import { TealiumEventProps } from "../helpers/analytics";

interface ChangeLocationProps {
  inline?: boolean;
  label?: string;
  lockModal?: boolean;
  open?: boolean;
  receiveCountyZip?: (county: County, zip: string) => void;
  showLocation?: boolean;
  tealiumOpenProps?: TealiumEventProps;
  tealiumSaveProps?: TealiumEventProps;
}

const ChangeLocation: FunctionComponent<ChangeLocationProps &
  RouteComponentProps> = ({
  label,
  lockModal,
  history,
  open,
  location,
  showLocation,
  inline,
  ...props
}) => {
  const t = useTranslate();
  const [showModal, setShowModal] = useState(open || false);
  const [newLocation, setNewLocation] = useState<{
    county: County;
    zipcode: string;
  } | null>(null);

  useEffect(() => {
    if (open !== undefined) {
      setShowModal(open);
    }
  }, [open]);

  const closeModal = (): void => setShowModal(false);
  const openModal = (): void => {
    if (props.tealiumOpenProps) {
      const { event_action, event_label } = props.tealiumOpenProps;
      // istanbul ignore next: tested elsewhere
      sendTealiumEvent(event_action, event_label);
    }
    setShowModal(true);
  };

  const { dispatch, state } = useContext(AppStore.AppContext);

  const handleLocationSelection = (county?: County, zipcode?: string): void => {
    if (county && county.fips && zipcode) {
      setNewLocation({ county, zipcode });
    } else {
      setNewLocation(null);
    }
  };

  /* istanbul ignore next: tested elsewhere */
  const updateLocation = (): void => {
    if (newLocation) {
      const { county, zipcode } = newLocation;

      if (props.receiveCountyZip) {
        props.receiveCountyZip(county, zipcode);
      } else {
        dispatch({ type: ActionType.UPDATE_COUNTY, payload: county });
        dispatch({ type: ActionType.UPDATE_FIPS, payload: county.fips });
        dispatch({ type: ActionType.UPDATE_ZIPCODE, payload: zipcode });

        if (props.tealiumSaveProps) {
          const { event_action, event_label } = props.tealiumSaveProps;
          // istanbul ignore next: tested elsewhere
          sendTealiumEvent(event_action, event_label);
        }

        const currentUrl = new URI(location.search);
        const { plan_type } = currentUrl.search(true);
        const newUrl = new URI(location.pathname);
        newUrl.addSearch("fips", county.fips);
        newUrl.addSearch("zipcode", zipcode);
        newUrl.addSearch("plan_type", plan_type || state.planType);
        history.push(newUrl.toString());
      }

      setNewLocation(null);
      closeModal();
    }
  };

  return (
    <div
      className={`ChangeLocation ${inline ? "ds-u-display--inline-block" : ""}`}
    >
      <div className="ChangeLocation__buttonGroup">
        {state.county && showLocation && (
          <div className="e2e-current-location ds-u-md-margin-right--2">{`${state.county.name}, ${state.county.state}`}</div>
        )}
        <Button
          className="ds-c-button--transparent LocationEditor__toggleButton mct-hide-print"
          onClick={openModal}
        >
          {label || t("pharmacy_selection.change_location.button")}
        </Button>
      </div>

      {showModal && (
        <Dialog
          onExit={closeModal}
          heading={t("pharmacy_selection.change_location.title")}
          className="ChangeLocationModal"
          headerClassName="ChangeLocationModal__header"
          escapeExits={!lockModal}
          underlayClickExits={!lockModal}
          actions={[
            <Button
              className="ds-u-margin-right--2"
              key="primary"
              variation="primary"
              disabled={!newLocation}
              onClick={updateLocation}
            >
              {t("save")}
            </Button>,
            <Button
              className={classNames("ds-u-text-decoration--none", {
                "ds-u-display--none": lockModal,
              })}
              key="cancel"
              onClick={closeModal}
            >
              {t("cancel")}
            </Button>,
          ]}
        >
          <p className="ChangeLocationModal__message">
            {t("change_location.message")}
          </p>
          <CountyPicker
            label={t("pharmacy_selection.change_location.label")}
            onCountySelect={handleLocationSelection}
            className="ds-u-margin-bottom--6"
          />
        </Dialog>
      )}
    </div>
  );
};

export default withRouter(ChangeLocation);
